import { FC } from 'react';

import { getAllPageSlugsPaths } from 'lib/contentful';
import { PageSettings } from 'types/pages/pageContainer.types';
import { type StorefrontPageType } from 'humanity/pages/storefrontPage/storefrontPage.types';
import StorefrontPage from 'humanity/pages/storefrontPage';
import { getStorefrontPageSSG } from 'humanity/pages/storefrontPage/storefrontPage.query';

/**
 *
 * When using this function, destructure the result before re-exporting. This
 * prevents a weird client-side navigation bug.
 *
 * Example:
 * const storefrontPage = generateStorefrontPage('partners');
 * const { getStaticProps, Page } = storefrontPage;
 *
 * export { getStaticProps };
 * export default Page;
 */
export function generateStorefrontPage(slug: string) {
  const Page: FC<{
    pageContent: StorefrontPageType;
    pageSettings: PageSettings;
  }> = ({ pageSettings, pageContent }) => (
    <StorefrontPage pageSettings={pageSettings} pageContent={pageContent} />
  );

  return {
    Page,
    getStaticProps: getStorefrontPageSSG(slug),
    getStaticPaths: async () => ({
      paths: await getAllPageSlugsPaths(slug),
      fallback: 'blocking',
    }),
  };
}

/**
 *
 * When using this function, destructure the result before re-exporting. This
 * prevents a weird client-side navigation bug.
 *
 * Example:
 * const storefrontPage = generateStorefrontChildPages('partners');
 * const { getStaticProps, getStaticPaths, Page } = storefrontPage;
 *
 * export { getStaticProps, getStaticPaths };
 * export default Page;
 */
export function generateStorefrontChildPages(slug: string) {
  return {
    ...generateStorefrontPage(`${slug}/`),
    getStaticPaths: async () => ({
      paths: await getAllPageSlugsPaths(slug),
      fallback: 'blocking',
    }),
  };
}
